/** @format */
import React from 'react';
import styled, { css } from 'styled-components';
import FaIcon from 'components/ui2/FaIcon';
import { fonts, colors } from 'theme';
import { injectIntl } from 'react-intl';
import { noop } from 'utils/functions/noop';
import PropTypes from 'prop-types';

const StyledButton = styled.button`
  background-color: transparent;
  color: ${props => props.color ? colors[props.color] : colors.primary};
  height: ${props => props.height};
  font-size:  ${props => fonts.size[props.fontSize]};
  padding: ${props => props.padding ? props.padding : '0.4em 1em'};
  border-radius: 0;
  outline: none !important;
  box-sizing: border-box;
  cursor: pointer;
margin: ${props => {
  if (props.mauto) return 'auto';
  if (props.margin) return props.margin;
  return '0';
}};

width: ${props => {
  if (props.autowidth) return 'auto';
  if (props.width) return props.width;
  return '100%';
}};
  display: ${props => props.display ? 'block' : 'initial'};
  align-items: center;
  text-transform: ${props => props.transform ? props.transform : 'uppercase'};
  letter-spacing: ${props => props.letterSpacing ? props.letterSpacing : '0.05em'};
  font-weight: ${props => props.weight ? props.weight : '600'};
  border: ${props => props.borderSize || '2px'} solid ${props => props.color ? colors[props.color] : colors.primary};
  transition: background-color .2s linear;
  outline: none;
  text-overflow: ellipsis;
  overflow: hidden;

  ${props => props.flexIcons &&
    css`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    
    div {
      text-align: center;
      flex-grow: 2;
    }
    `
  }

  &:hover, &:active, &:focus {
border-color: ${props => {
  if (props.hoverColor) return colors[props.hoverColor];
  if (props.color) return colors[props.color];
  return colors.primary;
}};
background-color: ${props => {
  if (props.hoverColor) return colors[props.hoverColor];
  if (props.color) return colors[props.color];
  return colors.primary;
}};
    color: ${colors.white};
  }

  ${props =>
    props.disabled &&
    css`
    border-color: ${colors.grey};
    color: ${colors.grey};
    cursor: default;
    &:hover, &:active, &:focus {
      background-color: transparent;
      color: ${colors.grey};
      border-color: ${colors.grey};
    }
  `}

  ${props =>
    props.isActive &&
    css`
border-color: ${properties => colors[properties.hoverColor] || colors[properties.color] || colors.primary};
background-color: ${properties => colors[properties.hoverColor] || colors[properties.color] || colors.primary};

    color: ${colors.white};
  `}

`;
StyledButton.displayName = 'StyledButton';
StyledButton.defaultProps = {
  default: true,
};

const Button = ({
  intl,
  label, // Remove once the internationalization is fully supported
  intlLabel,
  name,
  color,
  padding,
  margin,
  disabled,
  display,
  fontSize,
  autowidth,
  width,
  weight,
  type,
  hoverColor,
  isActive,
  disabledColor,
  iconLeft,
  iconRight,
  onClick = noop,
  transform,
  letterSpacing,
  borderSize,
  ariaLabel = label,
}) => {
  const buttonLabel = intlLabel ?
    intl.formatMessage({ id: intlLabel.id }, intlLabel.params) :
    label;

  const [ariaPressed, setAriaPressed] = React.useState('false');
  const onClickButton = (e) => {
    setAriaPressed('true');
    onClick(e);
  };

  return (
    <StyledButton
      aria-pressed={ariaPressed}
      aria-label={ariaLabel}
      color={color}
      borderSize={borderSize}
      display={display}
      hoverColor={hoverColor}
      name={name}
      type={type}
      disabled={disabled}
      disabledColor={disabledColor}
      fontSize={fontSize}
      padding={padding}
      weight={weight}
      margin={margin}
      autowidth={autowidth}
      width={width}
      isActive={isActive}
      flexIcons={iconLeft || iconRight}
      // onKeyUp={e => {
      //   e.preventDefault();
      //   e.stopPropagation();
      //   if (e.keyCode == 13) { e.target.click(); }
      // }}
      onClick={onClickButton}
      transform={transform}
      letterSpacing={letterSpacing}
    >
      {(iconLeft || iconRight) ? (
        <>
          {iconLeft && <FaIcon fontSize={fontSize} icon={iconLeft} />}
          <div>{buttonLabel}</div>
          {iconRight && <FaIcon fontSize={fontSize} icon={iconRight} />}
        </>
      ) : buttonLabel
      }
    </StyledButton>
  );
};

Button.displayName = 'Button';
Button.propTypes = {
  ariaLabel: PropTypes.string, // Label per aria-label (default: label)
  autowidth: PropTypes.bool, // larghezza automatica (default false)
  borderSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // dimensione bordo
  color: PropTypes.string, // colore (chiave da colors)
  disabled: PropTypes.bool, // bottone disabilitato
  disabledColor: PropTypes.string, // colore custom disabilitato (non usato nel css, ma dichiarato)
  display: PropTypes.bool, // se display block o iniziale
  fontSize: PropTypes.string, // chiave dimensione font in fonts.size
  hoverColor: PropTypes.string, // colore hover (chiave da colors)
  iconLeft: PropTypes.string, // nome icona a sinistra (stringa, tipo FontAwesome icon)
  iconRight: PropTypes.string, // nome icona a destra
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired, // fornito da injectIntl
  intlLabel: PropTypes.shape({
    id: PropTypes.string.isRequired,
    params: PropTypes.object,
  }), // etichetta i18n (id + params opzionali)
  isActive: PropTypes.bool, // stato attivo del bottone (colore e bordo)
  label: PropTypes.string, // etichetta testuale del bottone
  letterSpacing: PropTypes.string, // letter-spacing css
  margin: PropTypes.string, // margin css
  name: PropTypes.string, // name attributo html
  onClick: PropTypes.func, // funzione callback click
  padding: PropTypes.string, // padding css
  transform: PropTypes.string, // text-transform css
  type: PropTypes.oneOf(['button', 'submit', 'reset']), // tipo bottone html
  weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // font-weight css
  width: PropTypes.string, // width css
};

Button.defaultProps = {
  ariaLabel: undefined,
  autowidth: false,
  borderSize: '2px',
  color: 'primary',
  disabled: false,
  disabledColor: undefined,
  display: false,
  fontSize: 'm',
  hoverColor: undefined,
  iconLeft: undefined,
  iconRight: undefined,
  intlLabel: undefined,
  isActive: false,
  label: '',
  letterSpacing: '0.05em',
  margin: '0',
  name: undefined,
  onClick: noop,
  padding: '0.4em 1em',
  transform: 'uppercase',
  type: 'button',
  weight: '600',
  width: '100%',
};

export default injectIntl(Button);
